// This month (docs/mastermind-admin-plan.md 3.3): the open month between sessions (each action with the
// member's tick and note, Edit words, Set aside, Add to the month, or Start month 1 when none is open), and
// its review once a session of their group has been held since it was set: how each action went (filled in
// from the ticks), what carries, the next month's new actions and its session, closed in 1 write
// (hub.close_month), all or nothing, after the member view's 10-second hold with Undo, then on to the next member when
// Adam asks. With migration 80 on the database (has80), the open month's first line is the session it's set at, with
// Change (hub.set_month_session), in both modes, since that session decides when the review falls due.
import { useEffect, useId, useMemo, useState } from 'react'
import { Button, Card, Checkbox, Dialog, Field, Options, Select, TextArea, useAdmin } from '../ui/index.js'
import { dm, wdm } from '../../products/mastermind/log.js'
import { LIMITS } from '../../products/accelerator/limits.js'
import { MM, PANEL, actionLines } from './words.js'
import { editAction, issueActions, putBack, setAside, setMonthSession } from './data.js'
import { guaranteeMoved, lastTickOf, monthBefore, monthSessionChoices, nextMonthOf } from './section.js'

// The member's tick: the day, or no day when the old site's copy didn't keep it
export const tickedLine = (a, first) => (a.ticked || a.done_at ? (a.done_at ? PANEL.tickedOn(first, dm(a.done_at)) : PANEL.tickedNoDay(first)) : PANEL.notTicked)

const OUTCOMES = ['done', 'part_done', 'not_done']

export function Said({ line }) {
  if (!line) return null
  return <p className={line.bad ? 'ad-error amx-said' : 'amx-said'} role={line.bad ? 'alert' : 'status'}>{line.text}</p>
}

// The session a month is set at: their group's and every group's held sessions, newest first, or none
export function SessionPick({ label, value, onChange, choices, hint, error, disabled }) {
  const nowMs = Date.now()
  return (
    <Field label={label} plain hint={hint} error={error}>
      {props => (
        <Select {...props} value={value} onChange={onChange} disabled={disabled}
          options={[...choices.map(s => ({ value: s.id, label: PANEL.sessionOption(s, nowMs) })), { value: 'none', label: PANEL.noSession }]} />
      )}
    </Field>
  )
}

// The session a month is set at: the latest held, once the sessions have loaded, until Adam picks another
function useLatestSession(choices) {
  const [picked, setPicked] = useState(null)
  return [picked ?? (choices[0]?.id || 'none'), setPicked]
}

// The session the open month is set at (migration 80): 1 line with Change, which opens the picker in place. choices:
// the sessions it can be set at (section.js monthSessionChoices: none at or before the month before's start). held: a
// close is on its hold, so nothing changes until it's gone.
function MonthSession({ log, open, person, product, choices, ctx, held, onChanged }) {
  const { api } = useAdmin()
  const current = (open.sessions || []).find(x => x.set_at)?.session_id
    || (open.set_at ? (log.sessions || []).find(x => Date.parse(x.starts_at) === Date.parse(open.set_at))?.session_id : null) || null
  const allowed = monthSessionChoices(log, choices)
  // the session it's set at now, kept in the list when it isn't 1 of the choices (a session of another group), so the
  // picker starts at it
  const kept = current && !allowed.some(c => c.id === current)
    ? (log.sessions || []).filter(x => x.session_id === current).map(x => ({ id: x.session_id, starts_at: x.starts_at, cohort_id: x.cohort_id, group: x.cohort_id ? x.cohort_name : null }))
    : []
  const list = [...kept, ...allowed]
  const start = current && list.some(c => c.id === current) ? current : 'none'
  const [editing, setEditing] = useState(false)
  const [picked, setPicked] = useState(start)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState(null)
  const [done, setDone] = useState(null)
  useEffect(() => { setEditing(false); setDone(null); setProblem(null) }, [person, open.cycle_id])
  const day = open.set_at ? wdm(open.set_at) : null

  async function save() {
    if (busy || held) return
    setBusy(true); setProblem(null)
    const session = picked === 'none' ? null : picked
    try {
      const answer = await setMonthSession(api, { person, product, session }, { ...ctx, prevMonth: monthBefore(log) })
      const s = session ? list.find(c => c.id === session) : null
      const moved = guaranteeMoved(answer?.guarantee)
      setEditing(false)
      await onChanged()
      setDone(`${MM.monthSessionDone(open.month, s ? wdm(s.starts_at) : null)}${moved ? ` ${moved}` : ''}`)
    } catch (e) {
      setProblem(e.message)
    } finally {
      setBusy(false)
    }
  }

  // after a save the done line takes the plain line's place, with Change, until the page is opened again (the review of
  // 22 Oct: the session said once)
  if (!editing) {
    return (
      <div className="amx-month-session">
        <p className={done ? 'amx-meta amx-said' : 'amx-meta'} role={done ? 'status' : undefined}>
          {done || MM.monthSessionLine(open.month, day)}{' '}
          <Button kind="quiet" size="sm" disabled={Boolean(held)} onClick={() => { setPicked(start); setEditing(true); setDone(null) }}>{MM.monthSessionChange}</Button>
        </p>
      </div>
    )
  }
  return (
    <div className="amx-form amx-inline amx-month-session" role="group" aria-label={MM.monthSessionField(open.month)}>
      <SessionPick label={MM.monthSessionField(open.month)} hint={MM.monthSessionHint(open.month)} error={problem}
        value={picked} onChange={v => { setPicked(v); setProblem(null) }} choices={list} disabled={busy} />
      <span className="amx-row-actions">
        <Button kind="ink" size="sm" busy={busy} disabled={Boolean(held) || picked === start} onClick={save}>{MM.monthSessionSave}</Button>
        <Button size="sm" disabled={busy} onClick={() => { setEditing(false); setProblem(null) }}>{PANEL.cancel}</Button>
      </span>
    </div>
  )
}

// Set this action aside, with the reason
function AsideDialog({ action, first, onClose, onDone, ctx }) {
  const { api } = useAdmin()
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState(null)
  async function go() {
    if (busy) return
    if (!reason.trim()) { setProblem(MM.asideNeeded); return }
    setBusy(true); setProblem(null)
    try {
      await setAside(api, { action: action.action_id, reason }, ctx)
      await onDone(MM.asideDone)
    } catch (e) {
      setProblem(e.message)
      setBusy(false)
    }
  }
  return (
    <Dialog title={MM.asideTitle} onClose={onClose} busy={busy}
      footer={<><Button kind="ink" busy={busy} onClick={go}>{MM.asideYes}</Button><Button onClick={onClose} disabled={busy}>{MM.asideNo}</Button></>}>
      <p className="amx-dialog-quote">{action.body}</p>
      <Field label={MM.asideField} hint={MM.asideNote(first)} error={problem}>
        {props => <TextArea {...props} value={reason} onChange={v => { setReason(v); setProblem(null) }} rows={3} maxLength={300} placeholder={MM.asidePlaceholder} />}
      </Field>
    </Dialog>
  )
}

// 1 action between sessions: its words, tick, note and carry tag, with More: Edit words and Set aside
function OpenAction({ a, first, ctx, onDone, onAside }) {
  const { api } = useAdmin()
  const [more, setMore] = useState(false)
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(a.body)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState(null)
  const ids = useId()
  const aside = a.result === 'set_aside'

  async function save() {
    if (busy) return
    setBusy(true); setProblem(null)
    try {
      await editAction(api, { action: a.action_id, body: text }, ctx)
      setEditing(false)
      await onDone(PANEL.saved)
    } catch (e) { setProblem(e.message) } finally { setBusy(false) }
  }
  async function back() {
    if (busy) return
    setBusy(true); setProblem(null)
    try {
      await putBack(api, { action: a.action_id }, ctx)
      await onDone(MM.putBackDone(first))
    } catch (e) { setProblem(e.message) } finally { setBusy(false) }
  }

  return (
    <li className={`amx-action${aside ? ' amx-action--aside' : ''}${a.ticked ? ' amx-action--ticked' : ''}`}>
      <span className={`amx-tick${a.ticked ? ' amx-tick--on' : ''}`} aria-hidden="true" />
      <div className="amx-action-main">
        {editing
          ? (
            <div className="amx-form">
              <Field label={MM.editLabel} plain>
                {props => <TextArea {...props} value={text} onChange={setText} rows={2} maxLength={LIMITS.action} />}
              </Field>
              <span className="amx-row-actions">
                <Button kind="ink" size="sm" busy={busy} disabled={!text.trim()} onClick={save}>{PANEL.save}</Button>
                <Button size="sm" onClick={() => { setEditing(false); setText(a.body) }}>{PANEL.cancel}</Button>
              </span>
            </div>
          )
          : <span className="amx-action-body" id={`${ids}-b`}>{a.body}</span>}
        <span className="amx-action-lines">
          <span>{tickedLine(a, first)}</span>
          {a.hops === 1 && <span className="ad-tag">{MM.carriedFrom(a.first_month)}</span>}
          {a.hops > 1 && <span className="ad-tag">{MM.carriedTimes(a.first_month, a.hops)}</span>}
          {aside && <span className="ad-tag">{MM.setAsideTag(a.set_aside_reason || '')}</span>}
        </span>
        {a.note && <span className="amx-note">{MM.note(first, a.note)}</span>}
        {problem && <p className="ad-error" role="alert">{problem}</p>}
      </div>
      {!editing && (
        <div className="amx-action-side">
          {aside
            ? <Button size="xs" busy={busy} onClick={back} aria-describedby={`${ids}-b`}>{MM.putBack}</Button>
            : (
              <>
                <Button size="xs" aria-expanded={more} onClick={() => setMore(m => !m)} aria-describedby={`${ids}-b`}>{MM.more}</Button>
                {more && (
                  <span className="amx-more">
                    <Button size="xs" onClick={() => { setEditing(true); setMore(false) }} aria-describedby={`${ids}-b`}>{MM.editWords}</Button>
                    <Button size="xs" onClick={() => { setMore(false); onAside(a) }} aria-describedby={`${ids}-b`}>{MM.setAside}</Button>
                  </span>
                )}
              </>
            )}
        </div>
      )}
    </li>
  )
}

// Add to the open month, or start month 1 (or the next) when none is open
function AddActions({ person, product, first, open, nextMonth, choices, ctx, onDone }) {
  const { api } = useAdmin()
  const [text, setText] = useState('')
  const [session, setSession] = useLatestSession(choices)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState(null)
  const lines = actionLines(text)
  const tooLong = lines.some(l => l.length > LIMITS.action) || lines.length > 20

  async function go() {
    if (busy || !lines.length) return
    setBusy(true); setProblem(null)
    try {
      const answer = await issueActions(api, { person, product, bodies: lines, session: open ? null : (session === 'none' ? null : session) }, ctx)
      setText('')
      await onDone(MM.added(lines.length, answer?.month ?? open?.month ?? nextMonth, answer?.started_cycle))
    } catch (e) {
      setProblem(e.message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="amx-form amx-add" role="group" aria-label={open ? MM.addTo(open.month) : MM.startMonth(nextMonth)}>
      {open && <h3 className="amx-h3">{MM.addTo(open.month)}</h3>}
      {!open && <SessionPick label={PANEL.setAt} value={session} onChange={setSession} choices={choices} />}
      <Field label={MM.addLabel} plain hint={MM.addHint(first)} error={problem || (tooLong ? PANEL.tooMany : null)}>
        {props => <TextArea {...props} value={text} onChange={v => { setText(v); setProblem(null) }} rows={3} placeholder={MM.addPlaceholder} />}
      </Field>
      <span>
        <Button kind="ink" size="sm" busy={busy} disabled={!lines.length || tooLong} onClick={go}>
          {open ? MM.addButton(lines.length) : MM.startMonth(nextMonth)}
        </Button>
      </span>
    </div>
  )
}

// The review: how each action went, what carries, the next month's actions and its session, in 1 write. It opens
// with 1 line saying what's filled in from the ticks (the critique's admin 14); a Done action has no carry tick, and
// Set aside is behind More. Closing starts the member view's 10-second hold (Member.jsx, the critique's admin 8):
// onClose({ how, payload, draft }), and hub.close_month goes when the hold ends. draft: the form as it was, when Undo
// brings Adam back to it; problem: the database's refusal of the last close, said here.
function Review({ open, person, product, first, nextMonth, choices, next, held, draft, problem: refused, onClose, onCancel, onAside, lastMonth }) {
  const live = open.actions.filter(a => a.result !== 'set_aside')
  const [outcome, setOutcome] = useState(() => draft?.outcome || Object.fromEntries(live.map(a => [a.action_id, a.ticked ? 'done' : 'not_done'])))
  const [carry, setCarry] = useState(() => draft?.carry || Object.fromEntries(live.map(a => [a.action_id, !a.ticked])))
  const [text, setText] = useState(draft?.text || '')
  const [session, setSession] = useLatestSession(choices)
  const [more, setMore] = useState(null)
  const [problem, setProblem] = useState(null)
  const lines = actionLines(text)
  const tooLong = lines.some(l => l.length > LIMITS.action) || lines.length > 20
  const ids = useId()
  // an action set aside while reviewing drops out of the form; a new one keeps its filled-in answer
  useEffect(() => {
    setOutcome(o => Object.fromEntries(live.map(a => [a.action_id, o[a.action_id] ?? (a.ticked ? 'done' : 'not_done')])))
    setCarry(c => Object.fromEntries(live.map(a => [a.action_id, c[a.action_id] ?? !a.ticked])))
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (draft?.session) setSession(draft.session) }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const ticked = live.filter(a => a.ticked || a.done_at).length

  function go(how) {
    if (held) return
    if (live.some(a => !outcome[a.action_id])) { setProblem(MM.outcomesNeeded); return }
    setProblem(null)
    onClose({
      how,
      payload: {
        person, product,
        outcomes: live.map(a => ({ action: a.action_id, outcome: outcome[a.action_id], carry: outcome[a.action_id] !== 'done' && Boolean(carry[a.action_id]) })),
        bodies: how === 'last' ? [] : lines,
        session: session === 'none' ? null : session,
        openNext: how !== 'last',
      },
      draft: { outcome, carry, text, session },
    })
  }

  const said = problem || refused
  return (
    <div className="amx-review">
      <p className="amx-lede">{MM.reviewOpening(first, ticked, live.length)}</p>
      <ol className="amx-actions">
        {live.map(a => (
          <li key={a.action_id} className="amx-action amx-action--review">
            <span className={`amx-tick${a.ticked ? ' amx-tick--on' : ''}`} aria-hidden="true" />
            <div className="amx-action-main">
              <span className="amx-action-body" id={`${ids}-${a.action_id}`}>{a.body}</span>
              <span className="amx-action-lines">
                <span>{tickedLine(a, first)}</span>
                {a.hops === 1 && <span className="ad-tag">{MM.carriedFrom(a.first_month)}</span>}
                {a.hops > 1 && <span className="ad-tag">{MM.carriedTimes(a.first_month, a.hops)}</span>}
              </span>
              {a.note && <span className="amx-note">{MM.note(first, a.note)}</span>}
              <div className="amx-review-row">
                <Options row label={MM.outcomeLabel(a.body)} value={outcome[a.action_id]} disabled={Boolean(held)}
                  options={OUTCOMES.map(o => ({ value: o, label: MM.outcomes[o] }))}
                  onChange={v => { setOutcome(s => ({ ...s, [a.action_id]: v })); setCarry(s => ({ ...s, [a.action_id]: v !== 'done' })) }} />
                {/* only an action part done or not done carries over, so Done has no carry tick */}
                {outcome[a.action_id] !== 'done' && (
                  <Checkbox checked={Boolean(carry[a.action_id])} disabled={Boolean(held) || lastMonth}
                    onChange={v => setCarry(s => ({ ...s, [a.action_id]: v }))} aria-describedby={`${ids}-${a.action_id}`}>
                    {MM.carryTo(nextMonth)}
                  </Checkbox>
                )}
                <span className="amx-review-more">
                  <Button size="xs" aria-expanded={more === a.action_id} disabled={Boolean(held)} onClick={() => setMore(m => (m === a.action_id ? null : a.action_id))} aria-describedby={`${ids}-${a.action_id}`}>{MM.more}</Button>
                  {more === a.action_id && <Button size="xs" onClick={() => { setMore(null); onAside(a) }} aria-describedby={`${ids}-${a.action_id}`}>{MM.setAside}</Button>}
                </span>
              </div>
            </div>
          </li>
        ))}
      </ol>
      <Field label={MM.newActions(nextMonth)} plain error={tooLong ? PANEL.tooMany : null}>
        {props => <TextArea {...props} value={text} onChange={v => { setText(v); setProblem(null) }} rows={3} placeholder={MM.addPlaceholder} />}
      </Field>
      <SessionPick label={MM.setNextAt(nextMonth)} value={session} onChange={setSession} choices={choices} />
      <div className="amx-close-row">
        <div className="amx-row-actions">
          <Button kind="ink" disabled={Boolean(held) || tooLong} onClick={() => go(next ? 'next' : 'close')}>{next ? MM.closeAndGo(open.month, next.name) : MM.close(open.month)}</Button>
          {lastMonth && <Button disabled={Boolean(held)} onClick={() => go('last')}>{MM.closeLast(open.month)}</Button>}
          {onCancel && <Button kind="quiet" disabled={Boolean(held)} onClick={onCancel}>{MM.cancelReview}</Button>}
        </div>
        {/* under the black button: close and stay, as a text link */}
        {next && <span><Button kind="quiet" size="sm" disabled={Boolean(held) || tooLong} onClick={() => go('close')}>{MM.closeStay(first)}</Button></span>}
      </div>
      {said && <p className="ad-error" role="alert">{said}</p>}
    </div>
  )
}

// This month, as a card: between sessions, or its review. Its title is the month of actions ("Month 2's actions"),
// apart from the membership's month in the head, and its line says when it was set, the ticks and the last tick on
// any of their actions (the critique's admin 2 and 9). held: a close of this member's month is on its 10-second hold;
// draft: the review as it was before an Undo; problem: the database's refusal of the last close.
export function ThisMonth({ log, person, product, first, choices, next, ctx, held, draft, problem, onChanged, onClose, has80 = false }) {
  const open = (log.months || []).find(m => !m.closed_at) || null
  const flags = log.summary?.flags || {}
  const due = Boolean(open && flags.review_due && flags.review_due.cycle_id === open.cycle_id)
  const [reviewing, setReviewing] = useState(Boolean(draft))
  const [aside, setAsideFor] = useState(null)
  const [line, setLine] = useState(null)
  useEffect(() => { setReviewing(Boolean(draft)); setLine(null) }, [log.person?.person_id]) // eslint-disable-line react-hooks/exhaustive-deps
  // a review opened early ends with the month it was of: once the close has gone and the next month is the open 1, it
  // shows between sessions until its own review is due (Undo's draft keeps the same month open, so it stays)
  useEffect(() => { if (!draft) setReviewing(false) }, [open?.cycle_id]) // eslint-disable-line react-hooks/exhaustive-deps
  const nextMonth = nextMonthOf(log)
  const months = log.product?.term_months || 12
  const ended = ['ended', 'revoked'].includes(log.access?.status)
  const lastMonth = Boolean(open && (open.month >= months || ended))
  const review = open && (due || reviewing)
  const live = open ? open.actions.filter(a => a.result !== 'set_aside') : []
  const asideRows = open ? open.actions.filter(a => a.result === 'set_aside') : []
  const done = async text => { await onChanged(); setLine({ text }) }
  const setDay = open ? (open.set_at ? wdm(open.set_at) : dm(open.started_at)) : null
  const last = lastTickOf(log)
  // with 80 (has80) the card's first line says the session the month starts at, so the aside keeps the ticks alone
  const aside2 = useMemo(() => {
    if (!open) return null
    const ticked = live.filter(a => a.ticked).length
    return has80 ? MM.ticksAside(ticked, live.length, last) : MM.setAtAside(setDay, Boolean(open.set_at_session), ticked, live.length, last)
  }, [open, setDay, live, last, has80])

  return (
    <Card id="mm-this-month" title={open ? MM.monthTitle(open.month) : MM.tabs.month} aside={open ? aside2 : null} className="amx-this-month">
      {open && has80 && (
        <MonthSession log={log} open={open} person={person} product={product} choices={choices} ctx={ctx} held={held} onChanged={onChanged} />
      )}
      {!open && (
        <>
          <p className="amx-lede">{MM.noMonth}</p>
          <AddActions person={person} product={product} first={first} open={null} nextMonth={nextMonth} choices={choices} ctx={ctx} onDone={done} />
        </>
      )}
      {open && review && (
        <Review key={`${person}:${open.cycle_id}`} open={open} person={person} product={product} first={first} nextMonth={nextMonth} choices={choices}
          next={next} held={held} draft={draft} problem={problem} lastMonth={lastMonth} onAside={setAsideFor}
          onCancel={due ? null : () => setReviewing(false)}
          onClose={entry => onClose({ ...entry, month: open.month })} />
      )}
      {open && review && asideRows.length > 0 && (
        // set aside by agreement: out of the review, and Put it back brings 1 into it
        <ol className="amx-actions amx-actions--aside">
          {asideRows.map(a => <OpenAction key={a.action_id} a={a} first={first} ctx={ctx} onDone={done} onAside={setAsideFor} />)}
        </ol>
      )}
      {open && !review && (
        <>
          {!live.length && !asideRows.length && <p className="amx-muted">{MM.noActions}</p>}
          {(live.length > 0 || asideRows.length > 0) && (
            <ol className="amx-actions">
              {[...live, ...asideRows].map(a => <OpenAction key={a.action_id} a={a} first={first} ctx={ctx} onDone={done} onAside={setAsideFor} />)}
            </ol>
          )}
          <Said line={line} />
          <AddActions person={person} product={product} first={first} open={open} nextMonth={nextMonth} choices={choices} ctx={ctx} onDone={done} />
          {live.length > 0 && <span><Button kind="quiet" size="sm" onClick={() => { setReviewing(true); setLine(null) }}>{MM.reviewNow(open.month)}</Button></span>}
        </>
      )}
      {(!open || review) && <Said line={line} />}
      {aside && <AsideDialog action={aside} first={first} ctx={ctx} onClose={() => setAsideFor(null)}
        onDone={async text => { setAsideFor(null); await done(text) }} />}
    </Card>
  )
}
